<template>
    <div>        
        <div class="main-content" :style="fontFamilyStyle">
                <div class="wb-wrap">
                    <!-- Settings drawer removed, replaced with inline sliders -->
                    <div class="wb-sticky-header-container">
                        <!-- Row 1: Title & Recipient -->
                        <div class="wb-sticky-header">
                            <div class="wb-header-titles">
                              <div class="wb-title">{{ this.wbtitle || 'Wishboard' }}</div>
                              <div v-if="showRecipient" class="wb-recipient">For {{ this.wbrecipient }}</div>
                            </div>
                        </div>
                        <!-- Board is at its wish limit. Shown as board state rather than
                             as a reaction to a refused submission, so everyone learns it
                             on arrival instead of discovering it by typing a wish and
                             being told no.

                             Unlock is offered to everyone. StripeChargeView has never
                             checked ownership -- it takes board_id from the request body
                             and the webhook marks that board paid -- so any signed-in user
                             can already unlock any board. Gating this on allowEdit would
                             only have hidden a working pay path. The support-code dialog
                             stays owner-only, because PromoRedeemView does verify the
                             creator. -->
                        <div v-if="limitReached" class="wb-limit-banner" role="status">
                            <i class="bx bx-error-circle"></i>
                            <div class="wb-limit-banner-text">
                                <strong>This board is full</strong>
                                <span>
                                    It has reached its limit of {{ wishLimit }} wishes.
                                    Anyone can unlock this board, or contact the organiser
                                    to let them know it is full.
                                </span>
                            </div>
                            <div class="wb-limit-banner-actions">
                                <button class="wb-action-btn primary"
                                        @click="goToUpgrade" title="Unlock this board">
                                    <i class="bx bx-credit-card"></i><span>Unlock · $2.99</span>
                                </button>
                                <button v-if="allowEdit" class="wb-action-btn"
                                        @click="openLimitPanel = true"
                                        title="Redeem a support code">
                                    <i class="bx bx-ticket"></i><span>Support code</span>
                                </button>
                            </div>
                        </div>

                        <!-- Row 2: Actions -->
                        <div class="wb-actions-row">
                            <button v-if="!isLocked && !limitReached" class="wb-action-btn primary" @click="add_wish('Form cannot be submitted yet.', $event)" title="Add a Wish">
                              <i class="bx bx-plus"></i><span>Add Wish</span>
                            </button>
                        <button v-if="allowEdit" class="wb-action-btn" @click="toggleAdminBar" :class="{ active: showAdminBar }" title="Board Settings">
                          <i class="bx bx-cog"></i><span class="desktop-only">Settings</span>
                        </button>
                        <ShareButton />
                    </div>

                    <!-- Admin control bar -->
                    <transition name="slide-down">
                      <div class="wb-admin-bar" v-if="allowEdit && showAdminBar">
                        <button v-if="!isLocked" class="wb-action-btn" @click="toggleBackgroundSlider" title="Customize Background">
                          <i class="bx bx-image-alt"></i><span>Background</span>
                        </button>
                        <button v-if="!isLocked" class="wb-action-btn" @click="toggleFontSlider" title="Customize Font">
                          <i class="bx bx-font-family"></i><span>Font</span>
                        </button>
                        <button v-if="!isLocked" class="wb-action-btn" @click="call_lock('Calling unlocked code.', $event)" title="Lock board">
                          <i class="bx bx-lock-open"></i><span>Lock</span>
                        </button>
                        <button v-if="isLocked" class="wb-action-btn warn" @click="call_lock('Calling locked code.', $event)" title="Board locked">
                          <i class="bx bx-lock"></i><span>Unlock</span>
                        </button>
                      </div>
                    </transition>

                    <transition name="slide-down">
                        <div v-if="!isLocked && showBackgroundSlider" class="wb-slider-panel">
                            <div class="slider-scroll-container">
                                <img v-for="image in images" :key="image.id"
                                  @click="change_bg('selected this image.', image)"
                                  :src="image.thumbnailUrl"
                                  class="slider-thumbnail" />
                            </div>
                        </div>
                    </transition>

                    <transition name="slide-down">
                        <div v-if="!isLocked && showFontSlider" class="wb-slider-panel">
                            <div class="slider-scroll-container">
                                <div v-for="font in fonts" :key="font.id"
                                  class="slider-font-option"
                                  :class="{ active: selectedFont && selectedFont.name === font.name }"
                                  :style="{ fontFamily: font.stack || font.name }"
                                  @click="selectFont(font)">
                                  {{ font.name }}
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
            <div>
                <div id="bg" class="cards" :style="{ 'background-image': backgroundImage }">
                    <!-- Empty state: convert a blank wall into an invitation -->
                    <div v-if="!loadingWishes && wishes.length === 0" class="wb-empty">
                        <i class="bx bx-mail-send"></i>
                        <h2>No wishes yet</h2>
                        <p>Be the first to leave a message on this board.</p>
                        <button v-if="!isLocked" class="wb-action-btn primary" @click="add_wish('Form cannot be submitted yet.', $event)">
                            <i class="bx bx-plus"></i><span>Add the first wish</span>
                        </button>
                    </div>

                    <!-- Wish limit reached. Opened by the 402 from /wish/create/, so it
                         appears exactly when the backend has refused a wish rather than
                         on a guess. The owner can upgrade or redeem a support code. -->
                    <!-- Owner-only support panel. The "board is full" message and the
                         unlock action live in the sticky banner, which every visitor
                         sees; this exists only for the owner's support-code field, which
                         is genuinely privileged -- PromoRedeemView verifies the creator. -->
                    <div v-if="openLimitPanel && allowEdit && !isLocked" class="wb-limit-panel"
                         role="dialog" aria-modal="true" aria-labelledby="wb-limit-title"
                         @click.self="openLimitPanel = false">
                        <div class="wb-limit-inner">
                            <h3 id="wb-limit-title">Support code</h3>
                            <p class="wb-limit-body">
                                Redeeming a code raises this board's limit without a
                                payment.
                            </p>

                            <div class="wb-promo-row">
                                <input v-model="promoCode" class="wb-promo-input"
                                       type="text" placeholder="e.g. SUPPORT-AB12"
                                       autocomplete="off" spellcheck="false"
                                       @keyup.enter="redeemPromo" />
                                <button class="wb-action-btn primary" :disabled="promoBusy"
                                        @click="redeemPromo">
                                    <i class="bx bx-check"></i>
                                    <span>{{ promoBusy ? 'Applying…' : 'Apply' }}</span>
                                </button>
                            </div>
                            <p v-if="promoError" class="wb-promo-msg is-error">{{ promoError }}</p>
                            <p v-else-if="promoMessage" class="wb-promo-msg is-ok">{{ promoMessage }}</p>

                            <div class="wb-limit-actions">
                                <button class="wb-action-btn" @click="openLimitPanel = false">
                                    <span>Close</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <transition-group name="wishlist" tag="div" class="wb-wishlist">
                    <div v-for="wish in sortedWishes" :key="wish.id"
                      :ref="wish.id === justPostedId ? 'justPosted' : undefined"
                      :data-wish-id="wish.id"
                      class="wishcard" :class="{ 'is-selected': wish.selected, 'is-new': wish.id === justPostedId }"
                      @click="toggle_wish(wish)">

                      <!-- Delete button shown on single-click select (owner only, and only
                           once a live token is confirmed — see canRemove) -->
                      <div v-if="canRemove && wish.selected" class="wishcard-close" @click.stop="delete_wish(wish)" title="Remove this wish">
                        <i class="bx bx-trash"></i> Remove
                      </div>

                      <!-- Media: YouTube or image -->
                      <div v-if="wish.wish_image_url" class="wish-media">
                        <div v-if="isYouTubeUrl(wish.wish_image_url)" class="wish-yt" @click.stop="toggleYT(wish)">
                          <template v-if="!wish._ytPlaying">
                            <img
                              :src="'https://img.youtube.com/vi/' + getYouTubeId(wish.wish_image_url) + '/hqdefault.jpg'"
                              class="wish-yt-thumb"
                              alt="video"
                              @error="e => e.target.style.display='none'"
                            />
                            <div class="wish-yt-play">
                              <i class="bx bx-play-circle"></i>
                            </div>
                          </template>
                          <iframe
                            v-else
                            class="wish-yt-frame"
                            :src="wish.wish_image_url + '?autoplay=1'"
                            frameborder="0"
                            allow="autoplay; encrypted-media"
                            allowfullscreen
                          ></iframe>
                        </div>
                        <img v-else class="wish-img" :src="wish.wish_image_url" alt="" loading="lazy"
                          :style="naturalSize(wish)" @load="onWishImageLoad($event, wish)" />
                      </div>

                      <!-- Message body -->
                      <div class="wish-body">
                        <p class="wish-message">{{ wish.wisher_message }}</p>
                      </div>

                      <!-- Footer: avatar + name + date -->
                      <div class="wish-footer">
                        <div class="wish-avatar" :style="{ background: nameColor(wish.wisher_name) }">
                          {{ initials(wish.wisher_name) }}
                        </div>
                        <div class="wish-meta">
                          <span class="wish-name">{{ wish.wisher_name }}</span>
                          <span class="wish-date">{{ formatWishDate(wish.wish_message_date) }}</span>
                        </div>
                      </div>

                    </div>
                    </transition-group>
                </div>
            </div>     
            
            <!-- Add Wish Modal -->
            <transition name="modal-slide">
            <div v-if="showAddWishModal" class="wish-modal-backdrop" @click.self="closeAddWishModal">
              <div class="wish-modal">

                <div class="wish-modal-header">
                  <span class="wish-modal-title">Add a Wish</span>
                  <button class="wish-modal-close" @click="closeAddWishModal"><i class="bx bx-x"></i></button>
                </div>

                <div class="wish-modal-body">

                  <!-- Name -->
                  <div class="wf-field">
                    <label class="wf-label">Your name</label>
                    <input class="wf-input" type="text" v-model="newWish.wisher_name" placeholder="Your name" maxlength="50" />
                  </div>

                  <!-- Message -->
                  <div class="wf-field">
                    <label class="wf-label">Your wish message</label>
                    <textarea class="wf-input wf-textarea" v-model="newWish.wisher_message" placeholder="Write your wish..." :maxlength="250" rows="4"></textarea>
                    <span class="wf-charcount" :class="{ warn: (250 - newWish.wisher_message.length) <= 50 }">{{ 250 - newWish.wisher_message.length }}</span>
                  </div>

                  <!-- Media type radio -->
                  <div class="wf-field wf-radios">
                    <label class="wf-radio"><input type="radio" v-model="newWish.mediaType" value="gif" /> GIF</label>
                    <label class="wf-radio"><input type="radio" v-model="newWish.mediaType" value="youtube" /> YouTube</label>
                    <label class="wf-radio"><input type="radio" v-model="newWish.mediaType" value="flickr" /> Flickr</label>
                  </div>

                  <!-- GIF search -->
                  <div v-if="newWish.mediaType === 'gif'" class="wf-field">
                    <div class="wf-search-row">
                      <input class="wf-input" type="text" v-model="newWish.searchTerm" @keyup.enter="searchGifs" placeholder="Search GIFs..." />
                      <button class="wf-search-btn" @click="searchGifs" :disabled="newWish.giphyLoading">
                        <span v-if="newWish.giphyLoading && newWish.giphies.length === 0"><i class="bx bx-loader-alt bx-spin"></i> Searching...</span>
                        <span v-else>Search</span>
                      </button>
                    </div>
                    <div class="wf-gif-grid" :class="{ 'has-selection': !!newWish.selectedGifUrl }" v-if="newWish.giphies.length > 0" @scroll="onGiphyScroll">
                        <div v-for="g in newWish.giphies" :key="g.id"
                          class="wf-gif-item"
                          :class="{ selected: newWish.selectedGifId === g.id || newWish.selectedGifUrl === gifUrl(g) }"
                          @click="selectGif(g)">
                          <img :src="gifUrl(g)" loading="lazy" />
                          <div class="wf-gif-overlay"></div>
                          <div class="wf-gif-selected-badge">
                            <i class="bx bx-check"></i>
                          </div>
                        </div>
                    </div>
                    <div v-if="newWish.giphies.length > 0 && newWish.giphyHasMore" class="wf-gif-more-wrap">
                      <button class="wf-gif-more-btn" @click="loadMoreGifs" :disabled="newWish.giphyLoading">
                        <span v-if="newWish.giphyLoading"><i class="bx bx-loader-alt bx-spin"></i> Loading more...</span>
                        <span v-else><i class="bx bx-down-arrow-circle"></i> Load More GIFs</span>
                      </button>
                    </div>
                  </div>

                  <!-- YouTube -->
                  <div v-if="newWish.mediaType === 'youtube'" class="wf-field">
                    <label class="wf-label">YouTube video URL</label>
                    <div class="wf-search-row wf-url-row">
                      <input class="wf-input" type="text" v-model="newWish.youtubeUrl" @change="fetchYouTubeVideo" @blur="fetchYouTubeVideo" placeholder="Paste a YouTube link" />
                      <button class="wf-search-btn" @click="fetchYouTubeVideo" :disabled="!newWish.youtubeUrl">Fetch</button>
                    </div>
                    <p class="wf-hint">Paste a YouTube link, then tap Fetch. Works with watch, live, youtu.be, Shorts and embed links.</p>
                    <div v-if="newWish.youtubeError" class="wf-status wf-status-error">{{ newWish.youtubeError }}</div>
                    <div v-if="newWish.youtubeVideoId" class="wf-selected-preview" style="margin-top:8px;">
                      <img :src="'https://img.youtube.com/vi/' + newWish.youtubeVideoId + '/hqdefault.jpg'" style="max-height:100px;border-radius:8px;" />
                      <div style="display:flex;flex-direction:column;gap:2px;flex:1;">
                        <span class="wf-preview-title">YouTube Video Ready</span>
                        <span class="wf-preview-sub">ID: {{ newWish.youtubeVideoId }}</span>
                      </div>
                      <button class="wf-clear-btn" @click="newWish.youtubeVideoId='';newWish.youtubeResolvedUrl='';newWish.youtubeUrl=''"><i class="bx bx-x"></i></button>
                    </div>
                  </div>

                  <!-- Flickr -->
                  <div v-if="newWish.mediaType === 'flickr'" class="wf-field">
                    <label class="wf-label">Flickr photo page URL</label>
                    <div class="wf-search-row wf-url-row">
                      <input class="wf-input" type="text" v-model="newWish.flickrUrl" @change="fetchFlickrImage" @blur="fetchFlickrImage" placeholder="Paste a Flickr photo link" />
                      <button class="wf-search-btn" @click="fetchFlickrImage" :disabled="!newWish.flickrUrl">Fetch</button>
                    </div>
                    <p class="wf-hint">Paste the link to a Flickr photo page, then tap Fetch. Not a direct image URL.</p>
                    <div v-if="newWish.flickrResolving" class="wf-status">Fetching image...</div>
                    <div v-if="newWish.flickrError" class="wf-status wf-status-error">{{ newWish.flickrError }}</div>
                    <div v-if="newWish.flickrResolvedUrl" class="wf-selected-preview" style="margin-top:8px;">
                      <img :src="newWish.flickrResolvedUrl" style="max-height:100px;border-radius:8px;" />
                      <span class="wf-preview-label">Image found!</span>
                      <button class="wf-clear-btn" @click="newWish.flickrResolvedUrl='';newWish.flickrUrl=''"><i class="bx bx-x"></i></button>
                    </div>
                  </div>

                  <!-- Submit -->
                  <button class="wf-submit-btn" @click="submitWish" :disabled="!newWish.wisher_name || !newWish.wisher_message">
                    <i class="bx bx-send"></i> Post Wish
                  </button>

                </div>
              </div>
            </div>
            </transition>
        </div>
    </div>
</template>

<script>
// import axios from 'axios';
import { toast } from 'bulma-toast';
import { recipientAddsInfo } from '../utils/pricing'
import axios from '../axiosConfig';
import ShareButton from "@/components/ShareButton.vue"; // Adjust the path based on your project structure


export default {
    components: {
        ShareButton,
    },
    name: 'Wishboard',
    data () {
        return {
            // Wish-limit state. The board reports max_wish_count from the backend;
            // null means unknown (e.g. the list call has not returned yet) and
            // should not be treated as "full".
            wishLimit: null,
            wishCount: 0,
            limitReached: false,
            promoCode: '',
            promoBusy: false,
            promoMessage: '',
            promoError: '',
            openLimitPanel: false,
images:[
        { thumbnailUrl: require('@/assets/images/angel-4704518_1280_thumb.png'), original: () => import('@/assets/images/angel-4704518_1280.png') },
        { thumbnailUrl: require('@/assets/images/bee-7666216_1280_thumb.jpg'), original: () => import('@/assets/images/bee-7666216_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/boho-style-7314646_1280_thumb.png'), original: () => import('@/assets/images/boho-style-7314646_1280.png') },
        { thumbnailUrl: require('@/assets/images/chess-8348280_1280_thumb.jpg'), original: () => import('@/assets/images/chess-8348280_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/christmas-trees-8416168_1280_thumb.jpg'), original: () => import('@/assets/images/christmas-trees-8416168_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/cookies-8416230_1280_thumb.jpg'), original: () => import('@/assets/images/cookies-8416230_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/deer-8157052_1280_thumb.png'), original: () => import('@/assets/images/deer-8157052_1280.png') },
        { thumbnailUrl: require('@/assets/images/doodle-7898618_1280_thumb.jpg'), original: () => import('@/assets/images/doodle-7898618_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/floral-7887374_1280_thumb.jpg'), original: () => import('@/assets/images/floral-7887374_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/flowers-6924088_1280_thumb.png'), original: () => import('@/assets/images/flowers-6924088_1280.png') },
        { thumbnailUrl: require('@/assets/images/flowers-7910753_1280_thumb.png'), original: () => import('@/assets/images/flowers-7910753_1280.png') },
        { thumbnailUrl: require('@/assets/images/flowers-7911617_1280_thumb.jpg'), original: () => import('@/assets/images/flowers-7911617_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/flowers-8013886_1280_thumb.png'), original: () => import('@/assets/images/flowers-8013886_1280.png') },
        { thumbnailUrl: require('@/assets/images/flowers-8175044_1280_thumb.png'), original: () => import('@/assets/images/flowers-8175044_1280.png') },
        { thumbnailUrl: require('@/assets/images/flowers-8219261_1280_thumb.png'), original: () => import('@/assets/images/flowers-8219261_1280.png') },
        { thumbnailUrl: require('@/assets/images/hedgehog-7922370_1280_thumb.jpg'), original: () => import('@/assets/images/hedgehog-7922370_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/hut-6968718_1280_thumb.png'), original: () => import('@/assets/images/hut-6968718_1280.png') },
        { thumbnailUrl: require('@/assets/images/ice-cream-8081692_1280_thumb.png'), original: () => import('@/assets/images/ice-cream-8081692_1280.png') },
        { thumbnailUrl: require('@/assets/images/island-7983688_1280_thumb.jpg'), original: () => import('@/assets/images/island-7983688_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/landscape-7704393_1280_thumb.jpg'), original: () => import('@/assets/images/landscape-7704393_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/leaves-8184621_1280_thumb.png'), original: () => import('@/assets/images/leaves-8184621_1280.png') },
        { thumbnailUrl: require('@/assets/images/living-room-7939062_1280_thumb.png'), original: () => import('@/assets/images/living-room-7939062_1280.png') },
        { thumbnailUrl: require('@/assets/images/ornament-7923631_1280_thumb.png'), original: () => import('@/assets/images/ornament-7923631_1280.png') },
        { thumbnailUrl: require('@/assets/images/plants-7933494_1280_thumb.jpg'), original: () => import('@/assets/images/plants-7933494_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/plants-8264654_1280_thumb.jpg'), original: () => import('@/assets/images/plants-8264654_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/plastic-waste-7617451_1280_thumb.jpg'), original: () => import('@/assets/images/plastic-waste-7617451_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/sun-7522095_1280_thumb.jpg'), original: () => import('@/assets/images/sun-7522095_1280.jpg') },
        { thumbnailUrl: require('@/assets/images/tea-time-8100595_1280_thumb.jpg'), original: () => import('@/assets/images/tea-time-8100595_1280.jpg') }
        // { thumbnailUrl: require('@/assets/images/lighthouse-8184255_1280_thumb.jpg'), original: () => import('@/assets/images/lighthouse-8184255_1280.jpg') },
        // { thumbnailUrl: require('@/assets/images/night-7942715_1280_thumb.jpg'), original: () => import('@/assets/images/night-7942715_1280.jpg') },
        // { thumbnailUrl: require('@/assets/images/panda-7897774_1280_thumb.jpg'), original: () => import('@/assets/images/panda-7897774_1280.jpg') },
        // { thumbnailUrl: require('@/assets/images/pattern-8189525_1280_thumb.jpg'), original: () => import('@/assets/images/pattern-8189525_1280.jpg') },
        // { thumbnailUrl: require('@/assets/images/planet-7942329_1280_thumb.jpg'), original: () => import('@/assets/images/planet-7942329_1280.jpg') },
        // { thumbnailUrl: require('@/assets/images/rose-8240077_1280_thumb.jpg'), original: () => import('@/assets/images/rose-8240077_1280.jpg') },
      ],
            // { id: 27, url: 'https://cdn.pixabay.com/photo/2017/10/25/08/25/cupcakes-2887270_1280.jpg'},
        // ],
                

            // Only include fonts we actually load, and give each a fallback stack.
            // Previously this listed `cursive`, `fantasy`, `ui-rounded`, `Times New Roman`,
            // `fangsong`, `Georgia` and `Gill Sans` with "// Google Font" comments — none
            // were imported. They rendered via OS defaults, so `cursive`/`fantasy` differed
            // per platform and `ui-rounded` only worked on recent Apple OSes. Curated list:
            // every entry is a real webfont we load (see the @import above), and the stack
            // ends in broad system families so Tamil/Thai/Devanagari text still renders
            // instead of falling back to an arbitrary default.
            fonts: [
                { id: 1, name: 'Sofia',      stack: "'Sofia', 'Noto Sans Tamil', 'Noto Sans Thai', 'Noto Sans Devanagari', 'Kailasa', 'Latha', sans-serif" },
                { id: 2, name: 'Raleway',    stack: "'Raleway', 'Noto Sans Tamil', 'Noto Sans Thai', 'Noto Sans Devanagari', 'Kailasa', 'Latha', sans-serif" },
                { id: 3, name: 'Lora',       stack: "'Lora', 'Noto Serif Tamil', 'Noto Serif Thai', 'Noto Serif Devanagari', serif" },
                { id: 4, name: 'Playfair Display', stack: "'Playfair Display', 'Noto Serif Tamil', 'Noto Serif Thai', 'Noto Serif Devanagari', Georgia, serif" },
                { id: 5, name: 'Merriweather', stack: "'Merriweather', 'Noto Serif Tamil', 'Noto Serif Thai', 'Noto Serif Devanagari', Georgia, serif" },
                { id: 6, name: 'Inter',      stack: "'Inter', 'Noto Sans Tamil', 'Noto Sans Thai', 'Noto Sans Devanagari', sans-serif" },
                { id: 7, name: 'Cormorant Garamond', stack: "'Cormorant Garamond', 'Noto Serif Tamil', 'Noto Serif Thai', 'Noto Serif Devanagari', serif" },
                // ... add more fonts — add the @import above alongside any new entry
            ],
            locks: [
                {id:1, name: 'UnLocked'}
            ],
            share : [
                {id:1, name:'Copy URL'}
            ],
            selectedFont: null,
            wishes:[],
            loadingWishes: true,
            justPostedId: null,
            backgroundImage: '',
            wbid: '',
            wbtitle: '',
            wbrecipient: '',
            allowEdit: false,
            isLocked: false,
            showAdminBar: false,
            showBackgroundSlider: false,
            showFontSlider: false,
            showSidebar: false,
            activeTheme: '',
            collapseLock: true,
            collapseBackgroundImage: true,
            collapseFont: true,
            showAddWishModal: false,
            version: '0.1.0',
            newWish: {
              wisher_name: '',
              wisher_message: '',
              mediaType: 'gif',
              youtubeUrl: '',
              youtubeResolvedUrl: '',
              youtubeVideoId: '',
              youtubeError: '',
              flickrUrl: '',
              flickrResolvedUrl: '',
              flickrResolving: false,
              flickrError: '',
              selectedGifUrl: '',
              selectedGifId: '',
              searchTerm: '',
              giphies: [],
              giphyOffset: 0,
              giphyLimit: 24,
              giphyHasMore: false,
              giphyLoading: false,
            },

        }
    },
    computed: {
      // The 'For <name>' line is only useful when the title does not
      // already name them. Titles such as "Happy Birthday Keerthana Shri!"
      // otherwise print the name twice in the header -- which 203 of the boards
      // on record do today. Nothing is stored differently; this only decides
      // whether to draw the line.
      showRecipient() {
        return recipientAddsInfo(this.wbtitle, this.wbrecipient);
      },

    // Chronological, oldest first — a guestbook read top to bottom, ending on the
    // most recent wish. This matches the API's natural order (`vb - va` inverted).
    // An earlier version sorted newest-first, which put the last-added wish at the
    // top; on a board with ~49 wishes that buries the opening messages and makes the
    // wall read as "who arrived last" rather than the story of the board filling up.
    // Wishes with a missing or unparseable date sink to the bottom.
    sortedWishes() {
      return [...this.wishes].sort((a, b) => {
        const ta = new Date(a.wish_message_date || 0).getTime();
        const tb = new Date(b.wish_message_date || 0).getTime();
        const va = isNaN(ta) ? 0 : ta;
        const vb = isNaN(tb) ? 0 : tb;
        return va - vb;
      });
    },
    // Removing a wish is a gated, owner-only action that requires a live auth token.
    // `allowEdit` alone is not enough: it is set by an async validate/ call that can
    // resolve BEFORE the persisted gToken rehydrates from localStorage. That window
    // showed edit controls while the token was still empty, so the first Remove
    // fired with `Bearer ` and got a 403 — which looked like the board needing
    // several clicks per wish. Gating on the token means the control cannot appear
    // in a state where it is guaranteed to fail.
    canRemove() {
      return this.allowEdit && !this.isLocked && !!this.$store.getters.getGToken;
    },
    fontFamilyStyle() {
        // Resolve the saved/selected font name to its full fallback stack.
        // Boards store a bare name (e.g. "Sofia"); unknown or legacy names
        // (Helvetica, cursive, fangsong...) still render via a sane stack
        // instead of being dropped to an arbitrary browser default.
        const match = this.fonts.find(f => f.name === (this.selectedFont && this.selectedFont.name));
        const stack = match ? match.stack
          : ((this.selectedFont && this.selectedFont.name) || 'Sofia') + ", 'Noto Sans Tamil', 'Noto Sans Thai', 'Noto Sans Devanagari', sans-serif";
        return {
                fontFamily: stack,
        };
    },
    },

    methods: {
                // Never render an image larger than its native size.
                // Giphy returns a wide mix of resolutions (many GIFs are ~250px wide);
                // forcing them to the full column width magnified small GIFs — a 252x278
                // GIF shown at 432x474 is 1.71x and visibly pixelated. Once we know the
                // intrinsic size we cap width/height to it. Unknown size (image not yet
                // decoded) falls back to the CSS default of filling the column.
                naturalSize(wish) {
                    const d = wish && wish._naturalSize;
                    if (!d) return {};
                    return { width: d.w + 'px', height: d.h + 'px' };
                },
                onWishImageLoad(event, wish) {
                    const img = event.target;
                    if (!img || !img.naturalWidth) return;
                    this.$set(wish, '_naturalSize', { w: img.naturalWidth, h: img.naturalHeight });
                },
                // Bring the just-posted card into view. Masonry puts it at the bottom of
                // the last column, so without this the user never sees their own wish.
                //
                // Images must be decoded before scrolling: card heights are wrong until
                // every GIF has its intrinsic size, and `column-count` masonry reflows as
                // they load. Scrolling on nextTick alone measured a stale position and
                // landed ~3000px off, putting the new card well above the viewport.
                scrollToJustPosted() {
                    const self = this;
                    const attempt = (tries) => {
                        const el = self.$refs.justPosted;
                        const node = Array.isArray(el) ? el[0] : el;
                        if (!node || !node.scrollIntoView) {
                            if (tries > 0) setTimeout(() => attempt(tries - 1), 150);
                            return;
                        }
                        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setTimeout(() => { self.justPostedId = null; }, 4000);
                    };

                    // Wait for the card's own images, then one extra frame for the
                    // masonry reflow to settle.
                    const card = this.$refs.justPosted;
                    const node0 = Array.isArray(card) ? card[0] : card;
                    const imgs = node0 ? [...node0.querySelectorAll('img')].filter(i => !i.complete) : [];
                    let waited = 0;
                    const poll = setInterval(() => {
                        waited += 60;
                        const pending = imgs.filter(i => !i.complete).length;
                        if (pending === 0 || waited > 2000) {
                            clearInterval(poll);
                            requestAnimationFrame(() => setTimeout(() => attempt(5), 50));
                        }
                    }, 60);

                    // A single scrollIntoView is not enough. `column-count` masonry
                    // reflows as images decode, so the first scroll measures a stale
                    // offset and overshoots (measured: card ended ~3500px above the
                    // viewport). Re-issue it while the card is still off-screen, then
                    // stop as soon as it lands. Two passes settle it reliably.
                    let correction = 0;
                    const self2 = this;
                    const recheck = setInterval(() => {
                        const el = self2.$refs.justPosted;
                        const node = Array.isArray(el) ? el[0] : el;
                        if (!node) { clearInterval(recheck); return; }
                        const r = node.getBoundingClientRect();
                        const vh = window.innerHeight || document.documentElement.clientHeight;
                        const off = r.top < 0 || r.bottom > vh;
                        if (off && correction < 8) {
                            correction++;
                            node.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        } else {
                            clearInterval(recheck);
                        }
                    }, 220);
                },
                isYouTubeUrl(url) {
                      const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
                        return youtubeRegex.test(url);
                },
                getYouTubeId(url) {
                    // Delegates to the single parser so the two can't drift apart.
                    return this.youTubeIdFrom(url);
                },
                toggleYT(wish) {
                    wish._ytPlaying = !wish._ytPlaying
                },
                initials(name) {
                    if (!name) return '?'
                    return name.trim().split(/\s+/).map(w => w[0].toUpperCase()).slice(0, 2).join('')
                },
                nameColor(name) {
                    if (!name) return '#aaa'
                    const colors = ['#e8a020','#3b82f6','#10b981','#8b5cf6','#ef4444','#f59e0b','#06b6d4','#ec4899','#84cc16','#f97316']
                    let hash = 0
                    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
                    return colors[Math.abs(hash) % colors.length]
                },
                formatWishDate(dateStr) {
                    if (!dateStr) return ''
                    const d = new Date(dateStr)
                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                },
                toggle_wish(wish){
                    // Selection only exists to expose the Remove button, so it must follow
                    // the same gate. Selecting a card while the token is still rehydrating
                    // would otherwise draw the selection outline with no way to act on it.
                    if (!this.canRemove) return;
                    wish.selected = !wish.selected;
                },
                delete_wish(wish) {
                    // URL for the delete-message endpoint
                    const url = `/mywishboard/api/wishboard/${this.wbid}/message/${wish.id}/delete/`;

                    const token = this.$store.getters.getGToken;
                    if (!token) {
                        toast({
                            message: 'Still signing you in — try Remove again in a moment.',
                            type: 'is-warning', position: 'top-center',
                            duration: 4000, dismissible: true, pauseOnHover: true,
                        });
                        return;
                    }

                    // Optimistic removal: take the card out of the DOM immediately so the
                    // action feels instant. Previously the card only vanished once the
                    // server responded, and a rejected request failed silently (the .catch
                    // only console.error'd), so a 403 looked identical to "still working" and
                    // the board appeared to need several clicks per wish.
                    const index = this.wishes.indexOf(wish);
                    if (index === -1) return;          // already gone — don't double-submit
                    const removed = this.wishes.splice(index, 1)[0];

                    const restore = (why) => {
                        // Put it back where it was, preserving chronological order.
                        this.wishes.splice(index, 0, removed);
                        this.$nextTick(() => {
                            const el = this.$el.querySelector(`[data-wish-id="${removed.id}"]`);
                            if (el) el.classList.add('is-restored');
                        });
                        toast({
                            message: why,
                            type: 'is-danger', position: 'top-center',
                            duration: 4000, dismissible: true, pauseOnHover: true,
                        });
                    };

                    axios.delete(url, { headers: { 'Authorization': 'Bearer ' + token } })
                        .then(() => {
                            toast({
                                message: 'Wish removed.',
                                type: 'is-success', position: 'top-center',
                                duration: 2000, dismissible: true, pauseOnHover: true,
                            });
                        })
                        .catch(error => {
                            const status = error && error.response && error.response.status;
                            restore(
                                status === 403 || status === 401
                                    ? 'Not allowed to remove that wish — it has been put back.'
                                    : 'Could not remove that wish — it has been put back.'
                            );
                        });
                },
                // ── WISH LIMIT ─────────────────────────────────────
                // Reconcile limit state from a backend payload. Called from the 402
                // response and from the board-list response, so the panel can be
                // pre-filled rather than only appearing after a failed attempt.

                applyLimitState(body) {
                    if (!body) return;
                    if (typeof body.limit === 'number') this.wishLimit = body.limit;
                    if (typeof body.count === 'number') this.wishCount = body.count;
                    this.limitReached =
                        this.wishLimit !== null && this.wishCount >= this.wishLimit;
                },

                // The public wishlist endpoint returns wishes only -- no limit and
                // no entitlement -- because the board is shared with guests. So the
                // count comes from what we loaded, and the limit arrives with the
                // first 402. Until then wishLimit stays null and the UI shows nothing
                // rather than guessing.
                syncWishCountFromList() {
                    this.wishCount = this.wishes.length;
                    if (this.wishLimit !== null) {
                        this.limitReached = this.wishCount >= this.wishLimit;
                    }
                },

                // Only called after a create or a promo redeem. The initial state
                // comes from metadetails/get, which is the server's own answer and can
                // be newer than the wish list we already rendered.

                wishesRemaining() {
                    if (this.wishLimit === null) return null;
                    return Math.max(0, this.wishLimit - this.wishCount);
                },

                goToUpgrade() {
                    // Reuse the existing Stripe route rather than duplicating
                    // Payment.vue. It reads wishboard_id from the query.
                    this.$router.push({ path: '/payment', query: { wishboard_id: this.wbid } });
                },

                // Owner-only support path. Entitlement lives on the server, so the
                // board is refetched afterwards rather than trusted from the reply.
                redeemPromo() {
                    const code = (this.promoCode || '').trim();
                    if (!code || this.promoBusy) return;
                    this.promoBusy = true;
                    this.promoError = '';
                    this.promoMessage = '';

                    axios.post('/mywishboard/api/wishboard/promo/redeem/', {
                        code: code,
                        wishboard: this.wbid,
                    }, {
                        headers: {
                            'Authorization': 'Bearer ' + this.$store.getters.getGToken,
                            'X-CSRFToken': this.getCookie('csrftoken'),
                        },
                    })
                    .then(res => {
                        const data = res.data || {};
                        if (typeof data.wish_limit === 'number') {
                            this.wishLimit = data.wish_limit;
                            this.limitReached = false;
                        }
                        this.promoCode = '';
                        this.promoMessage = 'Unlocked — this board now takes up to ' +
                            data.wish_limit + ' wishes.';
                        toast({
                            message: this.promoMessage,
                            type: 'is-success',
                            position: 'top-center',
                            duration: 3500,
                            dismissible: true,
                        });
                        this.fetch(this.wbid);
                    })
                    .catch(err => {
                        const code2 = (err && err.response && err.response.data &&
                                       err.response.data.code) || '';
                        const messages = {
                            invalid_promo: 'That code is not valid.',
                            not_owner: 'Only the board owner can use a support code.',
                            unknown_wishboard: 'That board could not be found.',
                        };
                        this.promoError = messages[code2] || 'Could not apply that code.';
                    })
                    .finally(() => { this.promoBusy = false; });
                },

                validate_tok() {
                    // If there's no token, user is a guest — nothing to validate, stay in guest mode
                    if (!this.$store.getters.getGToken) {
                        return;
                    }

                    var url = "/mywishboard/api/wishboard/user/token/verify/";
                    // var url = "http://localhost:8000/mywishboard/api/wishboard/user/token/verify/"; dev enable; prod disable
                    
                    axios.post(url, {}, {
                        headers: {
                            'Authorization': 'Bearer ' + this.$store.getters.getGToken,
                        }
                    })
                    .then((response) => {
                        if (response.data && response.data.success === 'Token is available') {
                            console.log("Token is available");
                            // Handle the case where the token is available
                        } else if (response.data && response.data.error === 'Token expired') {
                            // Token expired — drop to guest mode instead of hard redirect.
                            // User keeps viewing/adding wishes; edit tools will hide since allowEdit relies on validate.
                            console.log("Token expired — dropping to guest mode");
                            this.$store.commit('setGToken', '');
                            this.$store.commit('setUser', '');
                            this.allowEdit = false;
                        } else if (response.data && response.data.error === 'User or Token error') {
                            // Same — drop to guest mode gracefully
                            console.log("User or Token error — dropping to guest mode");
                            this.$store.commit('setGToken', '');
                            this.$store.commit('setUser', '');
                            this.allowEdit = false;
                        } else {
                            // 'Token not available' or other — user is a guest, nothing to do
                        }
                    })
                    .catch((error) => {
                        // Network or server error — don't punish the user, just log it
                        console.error("Token validation error:", error);
                    });

                },
                // Single source of truth for YouTube IDs. Accepts every URL shape YouTube
                // hands out: /watch?v=ID (with any extra params), /live/ID, /shorts/ID,
                // /embed/ID, /v/ID, youtu.be/ID, and bare 11-char IDs. The old regex only
                // matched a literal 'watch?v=' and a fragile path-shaped alternative, so
                // /live/ links — which YouTube now uses for all live and many premieres —
                // were rejected, and the same failure appeared in getYouTubeId below.
                youTubeIdFrom(url) {
                    if (!url) return '';
                    const raw = String(url).trim();

                    // youtu.be/<id>
                    let m = raw.match(/(?:youtu\.be)\/([A-Za-z0-9_-]{11})(?:[?&#].*)?$/);
                    if (m) return m[1];

                    // youtube.com/watch?v=<id>  (also covers m./music.)
                    m = raw.match(/[?&]v=([A-Za-z0-9_-]{11})(?:[&#].*)?$/);
                    if (m) return m[1];

                    // Path forms: /live/<id>, /shorts/<id>, /embed/<id>, /v/<id>, /e/<id>
                    m = raw.match(/youtube\.com\/(?:live|shorts|embed|v|e)\/([A-Za-z0-9_-]{11})(?:[/?#].*)?$/);
                    if (m) return m[1];

                    // A bare 11-character id pasted on its own.
                    if (/^[A-Za-z0-9_-]{11}$/.test(raw)) return raw;

                    return '';
                },
                youtubefix(url) {
                    const id = this.youTubeIdFrom(url);
                    if (!id) throw new Error('Invalid YouTube URL');
                    return `https://www.youtube.com/embed/${id}`;
                },
                
                getCookie(name) {
                    // Get cookie value by name
                    const value = `; ${document.cookie}`;
                    const parts = value.split(`; ${name}=`);
                    if (parts.length === 2) return parts.pop().split(';').shift();
                },
            async handleVisibilityChange() {
                if (document.visibilityState === 'visible') {
                    // Page is visible, check authentication status and take appropriate action
                     this.validate_tok();
                }
            },


            copyURL() {
                const urlToCopy = window.location.href;
                this.copyToClipboard(urlToCopy);
                this.showToast("URL copied to clipboard! Share it with your friends");
                },
            copyToClipboard(text) {
                const textarea = document.createElement("textarea");
                textarea.value = text;
                textarea.style.position = "fixed";
                textarea.style.opacity = 0;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
                },
            showToast(message) {
                toast({
                    message: message,
                    type: 'is-success',
                    position: 'top-center',
                    duration: 3000,
                    dismissible: true,
                    pauseOnHover: true,
                });
            },
  

    async loadImage(image) {
        let thumbnail, original;
        thumbnail = image.thumbnailUrl; // static images
        original = (await image.original()).default; // dynamic images
        return { thumbnail, original };
    },
        toggleThemes(theme){
            if (this.activeTheme === theme) {
                this.activeTheme = ''; // Collapse the clicked theme if it's already active
            } else {
                this.activeTheme = theme; // Otherwise, set it as the active theme
            }

            this.collapseBackgroundImage = this.activeTheme !== 'background';
            this.collapseFont = this.activeTheme !== 'font';
            this.collapseLock = this.activeTheme !== 'lock';
            },

        shuffleImages() {
            // Fisher-Yates shuffle algorithm
            for (let i = this.images.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [this.images[i], this.images[j]] = [this.images[j], this.images[i]];
            }
        },

        call_lock(status){
            this.isLocked = !this.isLocked
            axios.post('/mywishboard/api/wishboard/usersettings/' + this.wbid + '/lock/',
              {
                  'user': this.$store.getters.getUser,
                  'is_locked': this.isLocked,
              },
              {
                    headers: { 
                        'Authorization': 'Bearer ' + this.$store.getters.getGToken,
                    }
              })
            
        },
          toggleAdminBar() {
            this.showAdminBar = !this.showAdminBar;
            if (!this.showAdminBar) {
                this.showBackgroundSlider = false;
                this.showFontSlider = false;
            }
        },
        toggleBackgroundSlider() {
            this.showBackgroundSlider = !this.showBackgroundSlider;
            if (this.showBackgroundSlider) {
                this.showFontSlider = false;
                this.shuffleImages();
            }
        },
        toggleFontSlider() {
            this.showFontSlider = !this.showFontSlider;
            if (this.showFontSlider) {
                this.showBackgroundSlider = false;
            }
        },
        toggleSidebar() {
            this.showSidebar = !this.showSidebar;
        },
          selectFont(font) {
            this.selectedFont = font;
              axios.post('/mywishboard/api/wishboard/usersettings/' + this.wbid + '/',
              {
                  'font_name': this.selectedFont.name,
              },
              {
                    headers: { 
                        'Authorization': 'Bearer ' + this.$store.getters.getGToken,
                    }
              })
        },

        call_sidebar: function (message, event){
            this.showSidebar = !this.showSidebar;
            this.shuffleImages();
        },

        add_wish: function (message, event) {
            if (event) {
                event.preventDefault()
            }
            this.showAddWishModal = true
        },
        async change_bg(message, image) {
            const { thumbnail, original } = await this.loadImage(image);
            this.backgroundImage = `url(${original})`;
    
            //   this.backgroundImage = 'url(https://cdn.pixabay.com/photo/2017/10/25/08/25/cupcakes-2887270_1280.jpg)';
            
            axios.post('/mywishboard/api/wishboard/usersettings/' + this.wbid + '/',
            {
                'font_name': this.selectedFont.name,
                ...(this.backgroundImage && { 'background_url': this.backgroundImage }),
            },
            {
                headers: { 
                    'Authorization': 'Bearer ' + this.$store.getters.getGToken,
                }
            })
          },


        fetch(wishboard_id) {
            this.loadingWishes = true;
            return axios.get('/mywishboard/api/wishlist/?wishboard_id=' + wishboard_id)
            .then((response) => {
                this.wishes = response.data;
                this.loadingWishes = false;
                console.log('Fetched wishes:', this.wishes.length);
                return this.wishes;
            })
            .catch(err => {
                this.loadingWishes = false;
                console.log('Error fetching wishes:', err);
                throw err;
            });
        },
        
        closeAddWishModal() {
            this.showAddWishModal = false
            // reset form
            this.newWish = { 
                wisher_name:'', 
                wisher_message:'', 
                mediaType:'gif', 
                youtubeUrl:'', 
                youtubeResolvedUrl:'', 
                youtubeVideoId:'', 
                youtubeError:'', 
                flickrUrl:'', 
                flickrResolvedUrl:'', 
                flickrResolving: false, 
                flickrError:'', 
                selectedGifUrl:'', 
                selectedGifId:'', 
                searchTerm:'', 
                giphies:[],
                giphyOffset: 0,
                giphyLimit: 24,
                giphyHasMore: false,
                giphyLoading: false,
            }
        },

        gifUrl(g) {
          const imgs = (g && g.images) || {};
          const a = (imgs.original && imgs.original.url) || '';
          const b = (imgs.downsized && imgs.downsized.url) || '';
          const c = (imgs.fixed_width && imgs.fixed_width.url) || '';
          return a || b || c;
        },

        selectGif(g) {
          const url = this.gifUrl(g);
          if (!url) return;

          // Toggle: clicking the already-selected GIF clears the selection.
          const isSelected = this.newWish.selectedGifId === g.id || this.newWish.selectedGifUrl === url;
          if (isSelected) {
            this.clearSelectedGif();
            return;
          }

          this.newWish.selectedGifId = g.id;
          this.newWish.selectedGifUrl = url;
        },

        clearSelectedGif() {
          this.newWish.selectedGifId = '';
          this.newWish.selectedGifUrl = '';
        },

        searchGifs() {
          if (!this.newWish.searchTerm.trim()) return;
          this.newWish.giphyOffset = 0;
          this.newWish.giphies = [];
          this.newWish.giphyHasMore = false;
          this.newWish.giphyLoading = true;
          this.fetchGiphyPage();
        },

        fetchGiphyPage() {
          const headers = this.$store.getters.getGToken ? { Authorization: 'Bearer ' + this.$store.getters.getGToken } : {};
          axios.get('/mywishboard/api/getgif/', {
            params: {
              search: this.newWish.searchTerm,
              offset: this.newWish.giphyOffset,
              limit: this.newWish.giphyLimit
            },
            headers
          })
          .then(res => { 
            if (res && res.data && res.data.data) {
              const newItems = res.data.data || [];
              this.newWish.giphies = this.newWish.giphyOffset === 0 ? newItems : [...this.newWish.giphies, ...newItems];
              const pagination = res.data.pagination || {};
              const total = pagination.total_count || 0;
              const count = pagination.count || newItems.length;
              this.newWish.giphyOffset += count;
              this.newWish.giphyHasMore = this.newWish.giphyOffset < total && newItems.length > 0;
            } else {
              if (this.newWish.giphyOffset === 0) {
                this.newWish.giphies = [];
              }
              this.newWish.giphyHasMore = false;
            }
          })
          .catch(err => console.error('GIF search error:', err))
          .finally(() => {
            this.newWish.giphyLoading = false;
          });
        },

        loadMoreGifs() {
          if (this.newWish.giphyLoading || !this.newWish.giphyHasMore) return;
          this.newWish.giphyLoading = true;
          this.fetchGiphyPage();
        },

        onGiphyScroll(e) {
          const target = e.target;
          if (target && target.scrollTop + target.clientHeight >= target.scrollHeight - 30) {
            this.loadMoreGifs();
          }
        },

        fetchYouTubeVideo() {
          const url = this.newWish.youtubeUrl.trim();
          if (!url) return;
          this.newWish.youtubeError = '';
          this.newWish.youtubeVideoId = '';
          this.newWish.youtubeResolvedUrl = '';
          try {
            const embedUrl = this.youtubefix(url);
            const videoId = this.getYouTubeId(url) || this.getYouTubeId(embedUrl);
            if (!videoId) throw new Error('Invalid YouTube URL');
            this.newWish.youtubeVideoId = videoId;
            this.newWish.youtubeResolvedUrl = embedUrl;
          } catch (e) {
            this.newWish.youtubeError = 'Invalid YouTube URL. Paste a link from YouTube (watch, live, shorts, embed or youtu.be).';
          }
        },


        async fetchFlickrImage() {
          const url = this.newWish.flickrUrl.trim();
          if (!url) return;
          this.newWish.flickrResolving = true;
          this.newWish.flickrError = '';
          this.newWish.flickrResolvedUrl = '';
          try {
            // Call our backend proxy to avoid CORS and handle URL cleaning
            const res = await axios.get('/mywishboard/api/flickr/resolve/', {
              params: { url }
            });
            const imageUrl = res.data.image_url;
            if (!imageUrl) throw new Error('No image URL returned');
            this.newWish.flickrResolvedUrl = imageUrl;
          } catch (e) {
            this.newWish.flickrError = 'Could not load image. Make sure it is a public flickr.com/photos/... URL.';
          } finally {
            this.newWish.flickrResolving = false;
          }
        },

        async submitWish() {
          // Validate input fields first
          if (!this.newWish.wisher_name.trim() || !this.newWish.wisher_message.trim()) {
            toast({
              message: 'Please fill in both name and wish fields.',
              type: 'is-warning',
              position: 'top-center',
              duration: 3000,
              dismissible: true,
              pauseOnHover: true,
            })
            return
          }

          // Determine media URL based on selection type
          let wish_image_url = ''
          if (this.newWish.mediaType === 'gif') {
            // Handle GIF selection more safely
            if (this.newWish.selectedGifUrl) {
              wish_image_url = this.newWish.selectedGifUrl;
            }
          } else if (this.newWish.mediaType === 'youtube') {
            if (!this.newWish.youtubeResolvedUrl && this.newWish.youtubeUrl.trim()) {
              this.fetchYouTubeVideo();
            }
            wish_image_url = this.newWish.youtubeResolvedUrl || (this.newWish.youtubeUrl.trim() ? this.youtubefix(this.newWish.youtubeUrl.trim()) : '');
          } else if (this.newWish.mediaType === 'flickr') {
            if (!this.newWish.flickrResolvedUrl && this.newWish.flickrUrl.trim()) {
              await this.fetchFlickrImage();
            }
            wish_image_url = this.newWish.flickrResolvedUrl || this.newWish.flickrUrl
          }

          // Prepare the payload data for wish creation
          const wishData = {
            wisher_name: this.newWish.wisher_name,
            wisher_message: this.newWish.wisher_message,
            wish_image_url,
            wishboard: this.wbid
          };

          console.log('Attempting to submit wish with data:', wishData);

          // Make the API request using axios for consistency
          axios.post('/mywishboard/api/wish/create/', wishData, {
            headers: {
              'Authorization': 'Bearer ' + this.$store.getters.getGToken,
              'X-CSRFToken': this.getCookie('csrftoken'),
            }
          })
          .then(response => {
            console.log("API Response received:", response.data);
            
            // Handle different possible response formats the API might return
            let success = false;
            if (response.data && typeof response.data === 'object') {
              // Check for various success indicators
              if (response.data["Wish created"] === "true" || 
                  response.data["Wish created"] === "true (duplicate ignored)" ||
                  response.data.success === true ||
                  response.data.id) {
                success = true;
              }
            } else if (response.data === "true") {
              // Handle string response case
              success = true;
            }
            
            if (success) {
              // Keep the remaining-wishes count honest as it grows.
              this.syncWishCountFromList();
              toast({
                message: 'Your wish has been added!',
                type: 'is-success',
                position: 'top-center',
                duration: 3000,
                dismissible: true,
                pauseOnHover: true,
              })
              
              // Prepend new wish to the board immediately.
              // The id MUST come from the server. It previously fell back to Date.now(),
              // which produced a 13-digit timestamp that the delete route can never
              // resolve — DELETE .../message/1790831293245/delete/ 404s because the
              // route looks up Wish.id. That is why a just-posted wish looked
              // undeletable while an existing one (with a real pk) deleted fine.
              // If the server still omits it, refetch the list and find the row
              // rather than inventing an id that can never be addressed.
              const serverId = response.data && response.data.id;
              if (serverId !== undefined && serverId !== null) {
                this.wishes.unshift({
                  id: serverId,
                  wisher_name: this.newWish.wisher_name,
                  wisher_message: this.newWish.wisher_message,
                  wish_image_url,
                  wish_message_date: new Date().toISOString(),
                  _ytPlaying: false,
                  selected: false,
                });
              } else {
                // Backend didn't return an id (older build). Reload the authoritative
                // list so the card exists with its real key.
                this.fetch(this.wbid);
              }

              this.closeAddWishModal()

              // Scroll the new card into view and highlight it. Wishes render
              // oldest-first, so a just-posted wish is the LAST in DOM order — which in a
              // column-count masonry layout puts it at the bottom of the rightmost column,
              // several screens down. Without this the user gets a success toast, a modal
              // that closes, and no visible confirmation that anything happened.
              // Skipped when we fell back to a refetch, since there is no local card to target.
              if (serverId !== undefined && serverId !== null) {
                this.justPostedId = serverId;
                this.$nextTick(() => {
                  this.scrollToJustPosted();
                });
              }
            } else {
              console.error("Failed to create wish - unexpected response format:", response.data);
              toast({
                message: 'Failed to add wish. Please try again.',
                type: 'is-danger',
                position: 'top-center',
                duration: 3000,
                dismissible: true,
                pauseOnHover: true,
              })
            }
          })
          .catch(err => {
            const status = err && err.response && err.response.status;
            const body = (err && err.response && err.response.data) || {};

            // The backend answers 402 with {code:'wish_limit_reached', limit, count}
            // when a board is full, and 404 when the board id is unknown. Those are
            // not transient failures, so the generic "try again" message was wrong:
            // retrying can never succeed, and the user would tap again forever.
            if (status === 402 && body.code === 'wish_limit_reached') {
              this.applyLimitState(body);

              // The wish was NOT added. Say so.
              //
              // openLimitPanel below only renders for the board owner -- it is
              // gated on allowEdit, because it holds the support-code field. So
              // for every guest the refusal was completely silent: the generic
              // "Failed to add wish" toast was deliberately removed for 402s
              // (retrying can never succeed, so "try again" was misleading), and
              // the only replacement was a dialog guests never see. The composer
              // stayed open with the text still in it, so it looked like it had
              // gone through.
              const limit = typeof body.limit === 'number' ? body.limit : null;
              toast({
                message: limit
                  ? 'This board is full at ' + limit + ' messages \u2014 your wish was not added.'
                  : 'This board is full \u2014 your wish was not added.',
                type: 'is-danger',
                position: 'top-center',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
              });

              // Close the composer so the state is unambiguous -- leaving it open
              // with the text still in it is what made the refusal look like a
              // silent no-op.
              //
              // closeAddWishModal() resets newWish, so the visitor would have to
              // retype if the owner unlocks the board. That is a deliberate
              // trade-off for now: keeping the draft would mean holding the GIF /
              // YouTube / Flickr selection state across a refusal, and those
              // resolvers have their own loading and error paths. If unlocks turn
              // out to be common, the better shape is to keep the composer open
              // but disabled, with a note explaining the board is full -- worth
              // revisiting once we know how often this path is hit in practice.
              this.closeAddWishModal();

              // The owner's support-code dialog still opens on top, for them only.
              if (this.allowEdit) this.openLimitPanel = true;
              return;
            }

            console.error('Submit wish error:', err);
            toast({
              message: status === 404
                ? 'That board could not be found.'
                : 'Failed to add wish. Please try again.',
              type: 'is-danger',
              position: 'top-center',
              duration: 3000,
              dismissible: true,
              pauseOnHover: true,
            })
          })
        },
        
    },
        
    mounted() {
            // Escape closes the support-code dialog. It is a fixed overlay now, so
            // it has to behave like one.
            this._limitPanelKeyHandler = (ev) => {
              if (ev.key === 'Escape' && this.openLimitPanel) this.openLimitPanel = false;
            };
            window.addEventListener('keydown', this._limitPanelKeyHandler);

            this.emitter.on('validateTokEvent', this.validate_tok);
    
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
            document.addEventListener('click', this.handleOutsideClick);

            this.emitter.emit('showlogout', 'true');
            this.emitter.emit('showWishboardCreate', 'true');
            this.emitter.emit('showWishboardList', 'true');

            // Get a random index within the range of the images array length
            // const randomIndex = Math.floor(Math.random() * this.images.length);
            // this.backgroundImage = this.images[randomIndex];

            var wbid = this.$store.state.wishboard_id_vue;
            wbid = this.$route.query.wishboard_id;
            this.wbid = wbid;
            this.fetch(wbid).then(() => {
                // The public wishlist endpoint returns wishes only, so the count the
                // limit panel shows comes from what we just loaded.
                this.syncWishCountFromList();
                var listorg = this.wishes;
                this.wishes = listorg.map((review) => {
                    return {
                        id: review.id,
                        wisher_name: review.wisher_name,
                        wisher_message: review.wisher_message,
                        wish_image_url: review.wish_image_url,
                        wish_message_date: review.wish_message_date,
                        _ytPlaying: false,
                        selected: false,
                        _naturalSize: null,
                    };
                });
                // Auto-resolve any legacy raw Flickr page URLs
                this.wishes.forEach(async (w) => {
                    if (w.wish_image_url && w.wish_image_url.includes('flickr.com/photos/') && !w.wish_image_url.includes('staticflickr.com')) {
                        try {
                            const res = await axios.get('/mywishboard/api/flickr/resolve/', { params: { url: w.wish_image_url } });
                            if (res.data && res.data.image_url) {
                                w.wish_image_url = res.data.image_url;
                            }
                        } catch (e) {
                            console.error('Failed to resolve legacy flickr url:', e);
                        }
                    }
                });
            });


                axios.post('/mywishboard/api/wishboard/metadetails/get/',
                    {
                        'wbid': wbid
                    },
                    {
                        headers: { 
                            'Authorization': 'Bearer ' + this.$store.getters.getGToken,
                        }
                    })
                    .then((response) => {
                        if(response.status >= 200){
                            this.wbtitle = response.data["wb_title"]
                            this.wbrecipient = response.data["wb_recipient"]
                            // Board-level limit state, known on arrival rather than
                            // after a refused submission.
                            if (typeof response.data["wish_limit"] === 'number') {
                                this.wishLimit = response.data["wish_limit"];
                            }
                            if (typeof response.data["wish_count"] === 'number') {
                                this.wishCount = response.data["wish_count"];
                            }
                            if (typeof response.data["limit_reached"] === 'boolean') {
                                this.limitReached = response.data["limit_reached"];
                            }
                        }
                    })




                    axios.get('/mywishboard/api/wishboard/usersettings/' + wbid +'/get/',
                    {
                        headers: { 
                            'Authorization': 'Bearer ' + this.$store.getters.getGToken,
                        }
                
                    })
                    .then((response) => {
                        if(response.status == 200){
                            this.backgroundImage = response.data["wishboard_background_url"];
                            const retrievedFontName = response.data["wishboard_font_name"];
                            this.selectedFont  = {name : retrievedFontName}
                        }else{
                            this.backgroundImage = 'url(https://cdn.pixabay.com/photo/2017/10/25/08/25/cupcakes-2887270_1280.jpg)';
                        }
                    });                


                    axios.post('/mywishboard/api/wishboard/usersettings/' + wbid +'/validate/',
                    {
                        'user':this.$store.getters.getUser
                    },
                    {
                        headers: { 
                            'Authorization': 'Bearer ' + this.$store.getters.getGToken,
                        }
                
                    })
                    .then((response) => {
                        if(response.status == 200){
                            if(response.data["wishboard_created_by_logged_user"] == true)
                                this.allowEdit = true;
                            else
                                this.allowEdit = false;
                        }else{
                            this.allowEdit = false;
                        }
                    });                

                    // check for locked board status

                    axios.get('/mywishboard/api/wishboard/usersettings/' + wbid +'/lockstatus/',
                    {
                        'user':this.$store.getters.getUser
                    },
                    {
                        headers: { 
                            'Authorization': 'Bearer ' + this.$store.getters.getGToken,
                        }
                    })
                    .then((response) => {
                        if(response.status == 200){
                            if(response.data["is_locked"] == true)
                                this.isLocked = true;
                            else
                                this.isLocked = false;
                        }
                    });                
 
    },

    beforeUnmount() {
        window.removeEventListener('keydown', this._limitPanelKeyHandler);
    },
}
</script>

<!-- Load Google Fonts used as board font options -->
<style>
@import "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Raleway:wght@400;600&family=Sofia&family=Lora:ital,wght@0,400;0,600;1,400&family=Playfair+Display:wght@400;600;700&family=Merriweather:wght@400;700&family=Cormorant+Garamond:wght@400;600;700&family=Noto+Sans+Tamil:wght@400;600&family=Noto+Serif+Tamil:wght@400;600&family=Noto+Sans+Thai:wght@400;600&family=Noto+Serif+Thai:wght@400;600&family=Noto+Sans+Devanagari:wght@400;600&family=Noto+Serif+Devanagari:wght@400;600&display=swap";
</style>

<style scoped>

/* ── SETTINGS DRAWER (Removed) ── */

/* ── ACTION BUTTONS ── */
/* ShareButton renders its own <button class="wb-action-btn"> inside a child
   component. Vue's scoped CSS appends a data-v attribute to the selector, so
   .wb-action-btn only matched buttons this component renders -- not the child's.
   Those buttons fell back to Bulma's default .button: system-ui at weight 400,
   13.3px, 1px 6px padding, 13px icons, next to Inter 700 pills. That mismatch was
   there before this rule; giving Share and Present visible labels is what made it
   obvious.

   :deep() drops the scope requirement for everything after it, so these must be
   written from an element this component owns. */
.wb-actions-row :deep(.wb-action-btn) {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  font-size: 0.82rem;
  font-weight: 700;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.wb-actions-row :deep(.wb-action-btn:hover) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.wb-actions-row :deep(.wb-action-btn i) {
  font-size: 1.1rem;
  line-height: 1;
}
.wb-actions-row :deep(.wb-action-btn span) {
  display: inline;
  margin: 0;
}
/* Confirmation state for the Share button. A brief tint, nothing more. */
.wb-actions-row :deep(.wb-action-btn.is-confirmed) {
  background: rgba(232, 160, 32, 0.12);
  border-color: rgba(232, 160, 32, 0.55);
  color: #b07010;
}
.wb-actions-row :deep(.wb-action-btn:focus-visible) {
  outline: 2px solid #e8a020;
  outline-offset: 2px;
}

.wb-action-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border-radius: 20px;
  border: 1px solid rgba(0,0,0,0.12);
  background: rgba(255,255,255,0.9);
  color: #333;
  font-size: 0.82rem;
  font-weight: 700;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.wb-action-btn i { font-size: 1.1rem; color: inherit; }
.wb-action-btn:hover { background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
.wb-action-btn.primary { background: #e8a020; border-color: #e8a020; color: #fff; }
.wb-action-btn.primary:hover { background: #d4911a; }
.wb-action-btn.active { background: #fdf3e0; border-color: #f0d898; color: #b07010; }
@media (max-width: 500px) { .desktop-only { display: none; } }
.wb-action-btn.warn { background: #fff5f5; border-color: #fdd; color: #e84040; }

@media (max-width: 500px) {
  .wb-action-btn span { display: none; }
  .wb-action-btn { padding: 7px 9px; }
  .side-panel { width: 100vw; }
}

/* ── STICKY BOARD HEADER ── */
.wb-sticky-header-container {
  position: fixed;
  top: calc(56px + env(safe-area-inset-top, 0px));
  left: 0;
  right: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
}

.wb-sticky-header {
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  padding: 12px 20px 8px;
  text-align: center;
}

.wb-actions-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 6px 20px 8px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
}

.wb-admin-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 8px 20px;
}

.admin-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 4px;
}

.wb-slider-panel {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0,0,0,0.07);
  padding: 10px 20px;
}

.slider-scroll-container {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  align-items: center;
  padding-bottom: 8px; /* For scrollbar */
}

.slider-scroll-container::-webkit-scrollbar {
  height: 6px;
}

.slider-scroll-container::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.slider-thumbnail {
  height: 60px;
  width: 60px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  border: 2px solid transparent;
  flex-shrink: 0;
  transition: transform 0.15s, border-color 0.15s;
}

.slider-thumbnail:hover {
  transform: scale(1.05);
  border-color: #e8a020;
}

.slider-font-option {
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid #ddd;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s;
  font-size: 1rem;
  color: #333;
}

.slider-font-option:hover {
  background: #fafafa;
}

.slider-font-option.active {
  background: #fdf3e0;
  border-color: #f0d898;
  color: #b07010;
}

.slide-down-enter-active, .slide-down-leave-active { transition: transform 0.25s ease, opacity 0.25s ease; }
.slide-down-enter-from, .slide-down-leave-to { transform: translateY(-10px); opacity: 0; }


.wb-header-titles {
  grid-column: 2;
  text-align: center;
}

.wb-title {
  font-size: 1.3rem;
  font-weight: 800;
  color: #0a0a0a;
  line-height: 1.2;
  font-family: 'Inter', -apple-system, sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wb-recipient {
  font-size: 0.85rem;
  color: #777;
  font-family: 'Inter', -apple-system, sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wb-actions {
  grid-column: 3;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-shrink: 0;
}

/* ── MASONRY BOARD ── */
/* The board iterates inside a <transition-group class="wb-wishlist">, so the
   cards are grandchildren of #bg.cards. #bg.cards is therefore the flex container
   with a single child, and every card ended up on its own row. Presentation.vue
   has no such wrapper and lays out directly, which is why only this view was
   affected. Flexing this wrapper instead puts the cards in the right context. */
.wb-wishlist {
  display: flex !important;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 28px;
  width: 100%;
}

#bg.cards {
    display: flex !important; /* row-major: reads left-to-right, wraps per row.
                                 Pre-redesign this was flex-wrap; the redesign
                                 switched to column-count, which fills each column
                                 top-to-bottom and so scrambled chronological order. */
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 28px; /* matches the previous column-gap so spacing is unchanged */
    padding: 195px 44px 80px !important;
    min-height: 100vh;
    background-size: auto;
    background-position: center;
    background-repeat: repeat;
    box-sizing: border-box;
  }

/* ── WISH CARD ── */
.wishcard {
  break-inside: avoid !important; /* inert under flex, keeps a card from splitting */
  width: auto !important;
  /* Row width: three across with the container's 28px gap, minus the card's own
     horizontal margin. Without an explicit basis here, `width: 100%` (still set
     for the column-count layout this replaced) makes every card a full-width row.
     calc uses percentage-of-container widths, so the margin is subtracted in
     proportion rather than in absolute pixels. */
  /* Basis for THREE across: subtract the two inter-card gaps the browser inserts
     between a 3-up row, then divide. `gap` is handled separately by flex, so it
     must not also be subtracted as card margin -- doing both made each card
     ~9px too wide and only two fitted per row.
     No `flex-basis: unset` either: it was inherited from the column-count
     layout where width:100% did the sizing. Under flex it silently discards the
     basis the `flex` shorthand just set (unset -> initial -> auto), putting every
     card on its own row. */
  /* The legacy stylesheet (styles_from_django.css) gives .wishcard
     `margin: 12px`. Under column-count that was invisible -- the column
     absorbed it -- but under flex-wrap it adds 24px of margin per card on top
     of the container's 28px gap, so three cards needed 1440px+ inside a
     1352px row and only two fit. Margin and gap were double-counting the same
     spacing. Drop the horizontal margin and let `gap` own it; the vertical
     margin-bottom is kept for row rhythm. */
  margin-left: 0 !important;
  margin-right: 0 !important;
  flex: 0 0 calc((100% - 56px) / 3) !important;
  max-width: 100% !important;
  max-height: unset !important;
  box-sizing: border-box;
}

/* Lift only — the old scale(1.015) widened the card past its column and made
   neighbours overlap during the transition. */
.wishcard:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.16);
  background: rgba(255, 255, 255, 0.97);
}

/* Selected state — owner only, and only to mark which card Remove will act on.
   Deliberately not red: the outline marks selection, it doesn't signal deletion. */
.wishcard.is-selected {
  outline: 2px solid #e8a020;
  outline-offset: 2px;
}

/* Delete button */
.wishcard-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px 5px 8px;
  background: #e84040;
  color: #fff;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  letter-spacing: 0.2px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(232, 64, 64, 0.4);
  transition: background 0.15s, transform 0.1s;
  user-select: none;
}

.wishcard-close:hover {
  background: #c0392b;
  transform: scale(1.05);
}

.wishcard-close i {
  font-size: 0.9rem;
  line-height: 1;
}

/* ── REMOVE / RESTORE ── */
/* Cards animate out on removal via Vue's <transition-group>, so the delete reads as
   deliberate rather than as the card blinking away. `.is-restored` flashes amber on the
   way back in when the server rejected the delete, so a failed removal is obvious
   instead of silently reverting. */
.wishlist-move {
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.wishcard-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
  /* collapse the gap the card leaves behind so neighbours slide up smoothly */
  max-height: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  overflow: hidden;
}
.wishcard-leave-to {
  opacity: 0;
  transform: scale(0.94) translateX(-14px);
}

.wishcard-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

@keyframes wish-restored {
  0%   { box-shadow: 0 0 0 0 rgba(232, 160, 32, 0.8), 0 10px 30px rgba(232,160,32,0.4); }
  70%  { box-shadow: 0 0 0 9px rgba(232, 160, 32, 0), 0 10px 30px rgba(232,160,32,0.4); }
  100% { box-shadow: 0 0 0 0 rgba(232, 160, 32, 0), 0 10px 30px rgba(232,160,32,0); }
}
.wishcard.is-restored {
  animation: wish-restored 1.1s ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .wishlist-move,
  .wishcard-leave-active,
  .wishcard-enter-active { transition: none; }
  .wishcard.is-restored { animation: none; outline: 2px solid #e8a020; }
}

/* ── EMPTY STATE ── */
/* Spans the full masonry width (column-count would otherwise confine it to
   one column) and forces the UI font — the board font is meant for card text. */
.wb-empty {
  /* Was `column-span: all`, which only means anything inside a multi-column
     container. The container is flex now, so this needs an explicit full basis to
     span the row; width alone left it sized as a single column. */
  flex: 0 0 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 90px 24px;
  text-align: center;
  font-family: 'Inter', sans-serif;
}
/* Direct child only. This rule exists to colour the big mail icon, but a
   descendant selector also caught the "+" inside the Add-the-first-wish button
   and painted it #e8a020 — the exact amber of the button's own background, so
   the glyph was invisible against it. The button's text stayed white because
   `.wb-action-btn.primary` only ever set colour on the button itself.

   Measured in the browser: icon rgb(232,160,32) on button rgb(232,160,32). */
.wb-empty > i {
  font-size: 3rem;
  color: #e8a020;
  opacity: 0.85;
}
/* ── WISH LIMIT BANNER ── */
/* Sits in the sticky header area, above the actions row, so it is visible the
   moment the board loads. Colours reuse the board's amber (#e8a020) and neutral
   greys rather than introducing a palette. */
.wb-limit-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 10px 0 4px;
  padding: 12px 16px;
  background: #fdf3e0;
  border: 1px solid #f0d898;
  border-radius: 12px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.wb-limit-banner > i {
  font-size: 1.5rem;
  color: #e8a020;
  flex-shrink: 0;
}
.wb-limit-banner-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
}
.wb-limit-banner-text strong {
  font-size: 0.92rem;
  font-weight: 800;
  color: #1a1a1a;
}
.wb-limit-banner-text span {
  font-size: 0.82rem;
  line-height: 1.5;
  color: #666;
}
.wb-limit-banner-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .wb-limit-banner { flex-wrap: wrap; }
  .wb-limit-banner-actions { width: 100%; }
  .wb-limit-banner-actions .wb-action-btn { flex: 1 1 auto; justify-content: center; }
}

/* ── WISH LIMIT PANEL ── */
/* Colours match the board's existing amber (#e8a020) and neutral greys rather
   than introducing a new palette. */
.wb-limit-panel {
  /* A real overlay: fixed to the viewport, scrimmed, centred, scrollable.

     It used to be `flex: 0 0 100%` inside the board's flex container, which made
     it ordinary page content sitting below the cards. role="dialog" said one
     thing and the layout said another, so after opening it the owner's attention
     went to the board rather than the form -- and the field they had to use was
     easy to miss entirely.

     Escape and a scrim click both close it, and the panel itself scrolls if it
     would overflow on a short viewport.
  */
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
  background: rgba(17, 17, 27, 0.45);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
/* Clicking the scrim closes; the card stops propagation so its controls still work. */
.wb-limit-panel .wb-limit-inner { position: relative; }
.wb-limit-inner {
  max-width: 460px;
  width: 100%;
  text-align: center;
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid #f0d898;
  border-radius: 16px;
  padding: 26px 24px 22px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.10);
}
.wb-limit-icon {
  font-size: 2.4rem;
  color: #e8a020;
}
.wb-limit-inner h3 {
  margin: 6px 0 8px;
  font-size: 1.15rem;
  font-weight: 800;
  color: #1a1a1a;
}
.wb-limit-body {
  margin: 0 0 18px;
  font-size: 0.92rem;
  line-height: 1.6;
  color: #666;
}
.wb-limit-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}
.wb-promo {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
.wb-promo-label {
  display: block;
  font-size: 0.76rem;
  color: #888;
  margin-bottom: 7px;
}
.wb-promo-row {
  display: flex;
  gap: 8px;
  justify-content: center;
}
.wb-promo-input {
  flex: 1 1 auto;
  max-width: 230px;
  padding: 7px 11px;
  font-size: 0.82rem;
  font-family: 'Inter', sans-serif;
  color: #333;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.wb-promo-input:focus {
  outline: none;
  border-color: #e8a020;
}
.wb-promo-msg {
  margin: 8px 0 0;
  font-size: 0.78rem;
}
.wb-promo-msg.is-error { color: #e84040; }
.wb-promo-msg.is-ok { color: #2e7d32; }

.wb-empty h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #1a1a1a;
}
.wb-empty p {
  margin: 0 0 6px;
  font-size: 0.95rem;
  color: #666;
}

/* ── JUST-POSTED WISH ── */
/* Confirmation that a post landed. The scroll alone can feel like a glitch, and the
   toast is gone after 3s — so the card itself signals it for 4s. Amber to match the
   selection outline rather than a new colour. */
.wishcard.is-new {
  box-shadow: 0 0 0 3px #e8a020, 0 12px 36px rgba(232, 160, 32, 0.35);
  animation: wish-just-posted 1.6s ease-out;
}

@keyframes wish-just-posted {
  0%   { box-shadow: 0 0 0 0 rgba(232, 160, 32, 0.85), 0 12px 36px rgba(232,160,32,0.35); }
  70%  { box-shadow: 0 0 0 10px rgba(232, 160, 32, 0), 0 12px 36px rgba(232,160,32,0.35); }
  100% { box-shadow: 0 0 0 3px #e8a020, 0 12px 36px rgba(232, 160, 32, 0.35); }
}

/* Motion-sensitive users get the static ring — the confirmation still reads, it
   just doesn't pulse. */
@media (prefers-reduced-motion: reduce) {
  .wishcard.is-new { animation: none; }
  .wishcard, .wf-gif-item, .wish-yt-thumb { transition: none; }
}

/* ── MEDIA ── */
/* Media sits flush to the card edge, so its radius must match the card's INNER
   clip radius (border-radius minus border width). At 16px vs a 15px inner clip the
   corners overshoot and get sliced — this is the "clipped at the top" effect.

   The image uses height:auto + max-height rather than a fixed box, so the
   wrapper grows with the image and nothing is cropped to fit. */
.wish-media {
  width: 100%;
  overflow: hidden;
  /* Must not exceed the card's inner clip radius (16px card radius - 1px border).
     15px is exact; anything larger slices the media corners. */
  border-radius: 15px 15px 0 0;
}

.wish-img {
  /* `width: 100%` alone would still upscale: the column is ~432px but many Giphy
     GIFs are ~250px wide. The component sets an inline width/height from the
     decoded natural size via naturalSize(), so images render at 1:1 or smaller
     and centre themselves in the card. `max-width/max-height` are the CSS safety
     net for when the natural size isn't known yet. */
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  margin: 0 auto;
  background: transparent;
  display: block;
}

/* YouTube */
.wish-yt {
  position: relative;
  width: 100%;
  cursor: pointer;
  background: #111;
}

/* hqdefault.jpg is 480x360 (4:3). The old fixed `height: 190px` + cover forced it
   into a 430x190 landscape box and cropped ~43% off the top and bottom. Render at the
   natural ratio and cap it instead. */
.wish-yt-thumb {
  width: 100%;
  height: auto;
  max-height: 480px;
  object-fit: cover;
  display: block;
  opacity: 0.9;
}

.wish-yt-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.18);
  transition: background 0.15s;
}

.wish-yt-play:hover { background: rgba(0,0,0,0.38); }

.wish-yt-play i {
  font-size: 4rem;
  color: #fff;
  filter: drop-shadow(0 2px 10px rgba(0,0,0,0.55));
}

/* Must match .wish-yt-thumb: hqdefault.jpg is 480x360 (4:3), so the natural
   rendered height at full card width is ~323px, not 220px. The iframe was
   hardcoded to 220px, so clicking a thumbnail shrank the card by ~103px and the
   video visibly jumped smaller as it started playing. Use aspect-ratio so the
   player holds the thumbnail's ratio at any card width. */
.wish-yt-frame {
  width: 100%;
  aspect-ratio: 4 / 3;
  max-height: 480px;
  display: block;
  border: none;
  background: #000;
}

/* ── MESSAGE BODY ── */
.wish-body {
  padding: 16px 18px 10px;
}

/* Fluid type. The layout is responsive (column-count 3 → 2 → 1) but the text was a
   fixed 1rem at every breakpoint, so on a phone the measure narrowed to ~22
   characters per line while the glyphs stayed desktop-sized. clamp() scales the
   message with the viewport instead of jumping at breakpoints.
   0.95rem floor → ~18.4px at a 1440px viewport → 1.15rem ceiling. */
.wish-message {
  font-size: clamp(0.95rem, 0.88rem + 0.35vw, 1.15rem);
  line-height: 1.75;
  color: #1a1a1a;
  margin: 0;
  white-space: pre-line;
  word-break: break-word;
  font-family: inherit;
}

/* ── CARD FOOTER ── */
.wish-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px 16px;
  border-top: 1px solid rgba(0,0,0,0.05);
  margin-top: 4px;
  font-family: inherit;
}

.wish-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.3px;
}

.wish-meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.wish-name {
  font-size: 0.85rem;
  font-weight: 700;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wish-date {
  font-size: 0.72rem;
  color: #bbb;
}

/* ── RESPONSIVE ── */
/* Breakpoint widened from 900px to 1200px for column count. At 1024px the 3-column
   layout gave ~293px cards — only ~28 characters per line, which is well below a
   comfortable measure and fights the fluid type. Two columns below 1200px keeps the
   measure around 35-40 characters. */
@media (max-width: 1200px) and (min-width: 601px) {
  #bg.cards { gap: 28px; padding: 160px 28px 60px !important; }
  .wishcard { flex: 0 0 calc((100% - 28px) / 2) !important; }
}

@media (max-width: 600px) {
  /* ── Sticky header: tighter on mobile ── */
  .wb-sticky-header { padding: 8px 12px 6px; }
  .wb-title { font-size: 1rem; }
  .wb-recipient { font-size: 0.78rem; }

  /* ── Actions row: wrap so Add Wish is always fully visible ── */
  .wb-actions-row {
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 12px 8px;
    justify-content: flex-start;
  }
  /* Add Wish takes full width on narrow screens so it's never clipped */
  .wb-actions-row .wb-action-btn.primary {
    flex: 1 1 100%;
    justify-content: center;
    font-size: 0.9rem;
    padding: 10px 14px;
  }
  /* Settings + Share sit side by side on the second row */
  .wb-actions-row .wb-action-btn:not(.primary) {
    flex: 1 1 auto;
    justify-content: center;
  }

  /* Admin bar wraps on mobile too */
  .wb-admin-bar {
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 12px;
    justify-content: flex-start;
  }
  .wb-admin-bar .wb-action-btn {
    flex: 1 1 auto;
    justify-content: center;
  }

  /* Cards: generous top padding to clear the taller wrapped header.
     env(safe-area-inset-bottom) handles iPhone notch/home-bar at the bottom. */
  #bg.cards {
    gap: 0;
    padding: 200px 12px calc(40px + env(safe-area-inset-bottom, 0px)) !important;
  }
  .wishcard { flex: 0 0 100% !important; margin-bottom: 14px; }
}

/* ── ADD WISH MODAL ── */
.wish-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 300;
  display: flex;
  align-items: flex-end;       /* bottom sheet on mobile */
  justify-content: center;
  backdrop-filter: blur(3px);
}

@media (min-width: 600px) {
  .wish-modal-backdrop { align-items: center; }  /* centered modal on desktop */
}

.wish-modal {
  background: #fff;
  width: 100%;
  max-width: 560px;
  /* On mobile, leave room for the iPhone home indicator (safe-area-inset-bottom).
     On desktop the 90vh cap is enough. */
  max-height: calc(90vh - env(safe-area-inset-bottom, 0px));
  border-radius: 20px 20px 0 0;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 -4px 40px rgba(0,0,0,0.2);
  /* Push content above the home bar on iPhone */
  padding-bottom: env(safe-area-inset-bottom, 0px);
  -webkit-overflow-scrolling: touch;
}

@media (min-width: 600px) {
  .wish-modal {
    border-radius: 20px;
    max-height: 90vh;
    padding-bottom: 0;
  }
}

.wish-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
  font-family: 'Inter', -apple-system, sans-serif;
}

.wish-modal-title { font-size: 0.95rem; font-weight: 800; color: #0a0a0a; }

.wish-modal-close {
  width: 30px; height: 30px; border-radius: 8px;
  border: 1px solid #eee; background: #fafafa;
  color: #666; font-size: 1.1rem;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
}

.wish-modal-body { padding: 20px; display: flex; flex-direction: column; gap: 16px; }

.wf-field { display: flex; flex-direction: column; gap: 6px; position: relative; }
.wf-label { font-size: 0.78rem; font-weight: 700; color: #555; font-family: 'Inter', sans-serif; }

.wf-input {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  padding: 10px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.15s;
  color: #111;
}
.wf-input:focus { border-color: #e8a020; }
.wf-textarea { resize: none; }

.wf-charcount {
  position: absolute; bottom: 8px; right: 10px;
  font-size: 0.7rem; color: #bbb; font-family: 'Inter', sans-serif;
}
.wf-charcount.warn { color: #e84040; }

.wf-radios { flex-direction: row; gap: 16px; }
.wf-radio {
  font-size: 0.82rem; font-weight: 600; color: #555;
  display: flex; align-items: center; gap: 5px;
  cursor: pointer; font-family: 'Inter', sans-serif;
}

.wf-search-row { display: flex; gap: 8px; }
/* URL fields: let the input take the free space so long links are readable,
   and stop the button from shrinking. */
.wf-url-row .wf-input { flex: 1 1 auto; min-width: 0; }
.wf-url-row .wf-search-btn { flex: 0 0 auto; white-space: nowrap; }
/* Explicit font-family: this modal renders inside the board, which applies the
   user's chosen font to card content. Without this the hint picks up the board
   font and stops looking like application UI. */
.wf-hint {
  font-family: 'Inter', sans-serif;
  font-size: 0.74rem;
  color: #888;
  margin: 0;
  line-height: 1.4;
}
.wf-search-btn {
  padding: 10px 14px; border-radius: 10px;
  border: 1px solid #e8a020; background: #fdf3e0;
  color: #b07010; font-size: 0.8rem; font-weight: 700;
  cursor: pointer; white-space: nowrap; font-family: 'Inter', sans-serif;
}

.wf-gif-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
  padding: 6px;
  border-radius: 12px;
  background: #f8f9fa;
  border: 1px solid #ebebeb;
}

.wf-gif-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  aspect-ratio: 1;
  background: #eee;
}

.wf-gif-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: all 0.25s ease;
}

/* When a selection exists in the grid: */
.wf-gif-grid.has-selection .wf-gif-item:not(.selected) {
  opacity: 0.35;
  filter: grayscale(85%) brightness(0.65) blur(1px);
  transform: scale(0.94);
}

.wf-gif-grid.has-selection .wf-gif-item:not(.selected):hover {
  opacity: 0.85;
  filter: grayscale(10%) brightness(0.95);
  transform: scale(0.98);
}

/* Selected GIF element styling */
.wf-gif-item.selected {
  border: 3px solid #e8a020 !important;
  box-shadow: 0 0 16px rgba(232, 160, 32, 0.65);
  transform: scale(1.04);
  z-index: 5;
  opacity: 1 !important;
  filter: none !important;
}

.wf-gif-selected-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  background: #e8a020;
  color: #fff;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.35);
  opacity: 0;
  transform: scale(0);
  transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.wf-gif-item.selected .wf-gif-selected-badge {
  opacity: 1;
  transform: scale(1);
}

.wf-gif-more-wrap {
  display: flex;
  justify-content: center;
  margin-top: 6px;
}

.wf-gif-more-btn {
  background: #fdf3e0;
  border: 1px solid #f0d898;
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #b07010;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
  font-family: 'Inter', sans-serif;
}

.wf-gif-more-btn:hover:not(:disabled) {
  background: #fae4be;
  color: #905500;
}

.wf-gif-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.wf-selected-preview {
  display: flex; align-items: center; gap: 10px;
  padding: 8px; background: #fafafa; border-radius: 10px;
  /* Explicit font-family: this modal renders inside the board, which applies the
     user's chosen font to everything beneath .main-content. Without this the
     "Image found!" / "YouTube Video Ready" labels inherit the board font and stop
     looking like application UI. */
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.wf-preview-title { font-size: 0.82rem; font-weight: 700; color: #111; }
.wf-preview-sub   { font-size: 0.75rem; color: #666; }
.wf-preview-label { font-size: 0.78rem; color: #555; flex: 1; }

/* Fetch status / error lines for the YouTube and Flickr fields. */
.wf-status {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.78rem;
  color: #888;
  margin-top: 4px;
}
.wf-status-error { color: #e84040; }
.wf-clear-btn {
  width: 24px; height: 24px; border-radius: 50%;
  border: 1px solid #eee; background: #fff;
  color: #999; font-size: 0.9rem;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
}

.wf-submit-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px; border-radius: 12px;
  background: #e8a020; border: none; color: #fff;
  font-size: 0.9rem; font-weight: 700; cursor: pointer;
  font-family: 'Inter', sans-serif;
  transition: background 0.15s;
}
.wf-submit-btn:hover { background: #d4911a; }
.wf-submit-btn:disabled { background: #f0d898; cursor: not-allowed; }

/* Modal slide transition */
  .modal-slide-enter-active, .modal-slide-leave-active { transition: transform 0.25s ease, opacity 0.25s ease; }
  .modal-slide-enter-from, .modal-slide-leave-to { transform: translateY(40px); opacity: 0; }
</style>

<!-- Version Footer -->
<div style="position: fixed; bottom: 0; width: 100%; background-color: #f8f9fa; padding: 10px; text-align: center; font-size: 12px; color: #6c757d;">
  Wishboard v{{ version }}
</div>
